"use client";

import { useMemo, useState } from "react";
import {
  allProducts,
  boutiques,
  allTags,
  stats,
  type Product,
} from "@/lib/products-data";
import { formatPrice, formatPriceCompact } from "@/lib/format";
import { productImageSquare } from "@/lib/product-image";
import { useCart } from "@/lib/cart-context";

type SortKey = "name" | "price-asc" | "price-desc" | "boutique";

const PAGE_SIZE = 24;

export function LaTableHyperOR() {
  const [query, setQuery] = useState("");
  const [boutiqueFilter, setBoutiqueFilter] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("boutique");
  const [page, setPage] = useState(1);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { addItem } = useCart();

  // Wrapper setters that also reset the page to 1 when a filter changes.
  const updateQuery = (v: string) => {
    setQuery(v);
    setPage(1);
  };
  const updateBoutiqueFilter = (v: string) => {
    setBoutiqueFilter(v);
    setPage(1);
  };
  const updateTagFilter = (v: string) => {
    setTagFilter(v);
    setPage(1);
  };
  const updateSort = (v: SortKey) => {
    setSort(v);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list: Product[] = allProducts.filter((p) => {
      if (boutiqueFilter !== "all" && p.boutiqueId !== boutiqueFilter)
        return false;
      if (tagFilter !== "all" && p.tag !== tagFilter) return false;
      if (q) {
        const haystack = `${p.name} ${p.origin} ${p.tag} ${p.description}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    list = [...list];
    switch (sort) {
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "name":
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "boutique":
      default:
        list.sort(
          (a, b) =>
            a.boutiqueId.localeCompare(b.boutiqueId) ||
            a.name.localeCompare(b.name)
        );
        break;
    }
    return list;
  }, [query, boutiqueFilter, tagFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const toggleFav = (id: string) =>
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const resetFilters = () => {
    setQuery("");
    setBoutiqueFilter("all");
    setTagFilter("all");
    setSort("boutique");
    setPage(1);
  };

  return (
    <section
      id="la-table"
      className="relative bg-ink text-cream py-24 md:py-32 overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 ink-gradient"></div>
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(200,162,74,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(200,162,74,0.6) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      ></div>
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(200,162,74,0.18) 0%, transparent 60%)",
        }}
      ></div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="ornament-line mb-6 justify-center">
            <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
              La table d&apos;Hyper OR
            </span>
          </div>
          <h2 className="font-display text-cream text-4xl md:text-6xl leading-[1.05]">
            Six cents produits,
            <br />
            <span className="gold-text italic">une seule table.</span>
          </h2>
          <p className="mt-6 text-cream/65 font-serif text-lg leading-relaxed">
            Le grand catalogue d&apos;Hyper OR I — toutes les références de nos
            douze boutiques réunies en une table interactive. Filtrez, cherchez,
            triez : la maison entière à portée de clic.
          </p>

          {/* KPIs */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <Kpi label="Boutiques" value={String(stats.boutiqueCount)} />
            <Kpi label="Produits" value={String(stats.productCount)} />
            <Kpi
              label="Premier prix"
              value={formatPriceCompact(stats.minPrice)}
              compact
            />
            <Kpi
              label="Prix d&apos;exception"
              value={formatPriceCompact(stats.maxPrice)}
              compact
            />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-cream/5 backdrop-blur-md border border-cream/15 rounded-2xl p-5 md:p-6 mb-8">
          <div className="grid md:grid-cols-12 gap-4 md:gap-5">
            {/* Search */}
            <div className="md:col-span-5">
              <label className="block text-cream/50 text-[10px] tracking-[0.25em] uppercase mb-2">
                Recherche
              </label>
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => updateQuery(e.target.value)}
                  placeholder="Rechercher un produit, une origine, une maison…"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-ink/60 border border-cream/15 text-cream placeholder:text-cream/40 text-sm focus:outline-none focus:border-gold transition-colors"
                />
              </div>
            </div>

            {/* Boutique select */}
            <div className="md:col-span-3">
              <label className="block text-cream/50 text-[10px] tracking-[0.25em] uppercase mb-2">
                Boutique
              </label>
              <select
                value={boutiqueFilter}
                onChange={(e) => updateBoutiqueFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-ink/60 border border-cream/15 text-cream text-sm focus:outline-none focus:border-gold transition-colors appearance-none cursor-pointer"
              >
                <option value="all">Toutes les boutiques</option>
                {boutiques.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tag select */}
            <div className="md:col-span-2">
              <label className="block text-cream/50 text-[10px] tracking-[0.25em] uppercase mb-2">
                Catégorie
              </label>
              <select
                value={tagFilter}
                onChange={(e) => updateTagFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-ink/60 border border-cream/15 text-cream text-sm focus:outline-none focus:border-gold transition-colors appearance-none cursor-pointer"
              >
                <option value="all">Toutes</option>
                {allTags.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="md:col-span-2">
              <label className="block text-cream/50 text-[10px] tracking-[0.25em] uppercase mb-2">
                Trier par
              </label>
              <select
                value={sort}
                onChange={(e) => updateSort(e.target.value as SortKey)}
                className="w-full px-3 py-2.5 rounded-lg bg-ink/60 border border-cream/15 text-cream text-sm focus:outline-none focus:border-gold transition-colors appearance-none cursor-pointer"
              >
                <option value="boutique">Boutique</option>
                <option value="name">Nom A→Z</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
              </select>
            </div>
          </div>

          {/* Active filters / clear */}
          <div className="mt-4 pt-4 border-t border-cream/10 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-cream/40 text-[10px] tracking-[0.25em] uppercase">
                {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
              </span>
              {favorites.size > 0 && (
                <span className="text-gold text-[10px] tracking-[0.25em] uppercase border border-gold/30 px-2 py-0.5 rounded-full">
                  {favorites.size} favori{favorites.size > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <button
              onClick={resetFilters}
              className="text-cream/60 hover:text-gold text-[10px] tracking-[0.25em] uppercase border-b border-cream/20 hover:border-gold pb-0.5 transition-colors"
            >
              Réinitialiser les filtres
            </button>
          </div>
        </div>

        {/* Table — desktop view */}
        <div className="hidden md:block bg-cream/3 backdrop-blur-sm border border-cream/15 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-ink/60 text-cream/70 text-[10px] tracking-[0.2em] uppercase">
                <th className="text-left font-medium px-5 py-4 w-12"></th>
                <th className="text-left font-medium px-5 py-4 w-20"></th>
                <th className="text-left font-medium px-5 py-4">Produit</th>
                <th className="text-left font-medium px-5 py-4">Boutique</th>
                <th className="text-left font-medium px-5 py-4 hidden lg:table-cell">
                  Origine
                </th>
                <th className="text-left font-medium px-5 py-4 hidden lg:table-cell">
                  Catégorie
                </th>
                <th className="text-right font-medium px-5 py-4">Prix</th>
                <th className="text-right font-medium px-5 py-4 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((p, idx) => {
                const isFav = favorites.has(p.id);
                const boutique = boutiques.find((b) => b.id === p.boutiqueId);
                return (
                  <tr
                    key={p.id}
                    className={`border-t border-cream/8 hover:bg-cream/5 transition-colors ${
                      idx % 2 === 1 ? "bg-cream/[0.02]" : ""
                    }`}
                  >
                    <td className="px-5 py-4">
                      <button
                        onClick={() => toggleFav(p.id)}
                        aria-label="Favori"
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-all ${
                          isFav
                            ? "text-gold"
                            : "text-cream/30 hover:text-cream/70"
                        }`}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill={isFav ? "currentColor" : "none"}
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"></path>
                        </svg>
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div
                        className="relative h-14 w-14 rounded-lg overflow-hidden shrink-0 border border-cream/15"
                        style={{
                          background: `linear-gradient(135deg, ${
                            boutique?.accent ?? "#c8a24a"
                          }22 0%, transparent 100%)`,
                        }}
                      >
                        <img
                          src={productImageSquare(p)}
                          alt={p.name}
                          loading="eager"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-serif text-cream text-[15px] leading-snug">
                        {p.name}
                      </div>
                      <div className="text-cream/40 text-[11px] mt-0.5 line-clamp-1">
                        {p.description}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase"
                        style={{ color: boutique?.accent ?? "#c8a24a" }}
                      >
                        <span className="text-sm">
                          {boutique?.icon ?? "✦"}
                        </span>
                        {boutique?.name ?? p.boutiqueName}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-cream/65 hidden lg:table-cell">
                      {p.origin}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] tracking-[0.15em] uppercase border border-cream/15 text-cream/60">
                        {p.tag}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-display text-cream text-lg whitespace-nowrap">
                      {formatPrice(p.price)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        aria-label={`Ajouter ${p.name} au panier`}
                        onClick={() =>
                          addItem({
                            id: p.id,
                            name: p.name,
                            price: p.price,
                            image: productImageSquare(p),
                            origin: p.origin,
                            boutiqueName: p.boutiqueName,
                          })
                        }
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-all"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14"></path>
                          <path d="M12 5v14"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-16 text-center text-cream/50 font-serif italic"
                  >
                    Aucun produit ne correspond à votre recherche. Affinez vos
                    filtres ou réinitialisez.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden space-y-3">
          {pageItems.map((p) => {
            const isFav = favorites.has(p.id);
            const boutique = boutiques.find((b) => b.id === p.boutiqueId);
            return (
              <div
                key={p.id}
                className="bg-cream/5 border border-cream/15 rounded-xl p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="relative h-16 w-16 rounded-lg overflow-hidden shrink-0 border border-cream/15"
                      style={{
                        background: `linear-gradient(135deg, ${
                          boutique?.accent ?? "#c8a24a"
                        }22 0%, transparent 100%)`,
                      }}
                    >
                      <img
                        src={productImageSquare(p)}
                        alt={p.name}
                        loading="eager"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display =
                            "none";
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase mb-1"
                        style={{ color: boutique?.accent ?? "#c8a24a" }}
                      >
                        <span>{boutique?.icon ?? "✦"}</span>
                        <span className="truncate">
                          {boutique?.name ?? p.boutiqueName}
                        </span>
                      </div>
                      <h4 className="font-serif text-cream text-[15px] leading-snug">
                        {p.name}
                      </h4>
                      <p className="text-cream/40 text-[11px] mt-1 line-clamp-2">
                        {p.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-gold text-lg">
                      {formatPrice(p.price)}
                    </div>
                    <button
                      onClick={() => toggleFav(p.id)}
                      aria-label="Favori"
                      className={`mt-1 inline-flex h-7 w-7 items-center justify-center rounded-full transition-all ${
                        isFav
                          ? "text-gold"
                          : "text-cream/30 hover:text-cream/70"
                      }`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill={isFav ? "currentColor" : "none"}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-cream/8 flex items-center justify-between text-[10px] tracking-[0.15em] uppercase">
                  <span className="text-cream/60">{p.origin}</span>
                  <span className="text-cream/40">{p.tag}</span>
                </div>
              </div>
            );
          })}
          {pageItems.length === 0 && (
            <div className="text-center text-cream/50 font-serif italic py-12">
              Aucun produit ne correspond à votre recherche.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
            <div className="text-cream/40 text-[11px] tracking-[0.25em] uppercase">
              Page {currentPage} / {totalPages} · {filtered.length} produits
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-cream/15 text-cream/70 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="Précédent"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6"></path>
                </svg>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (n) =>
                    n === 1 ||
                    n === totalPages ||
                    Math.abs(n - currentPage) <= 1
                )
                .map((n, idx, arr) => {
                  const prev = arr[idx - 1];
                  const showEllipsis = prev && n - prev > 1;
                  return (
                    <span key={n} className="flex items-center">
                      {showEllipsis && (
                        <span className="px-1 text-cream/30">…</span>
                      )}
                      <button
                        onClick={() => setPage(n)}
                        className={`h-9 min-w-9 px-2 rounded-full text-xs tracking-wider transition-all ${
                          currentPage === n
                            ? "bg-gold text-ink font-semibold"
                            : "text-cream/70 hover:bg-cream/10"
                        }`}
                      >
                        {n}
                      </button>
                    </span>
                  );
                })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-cream/15 text-cream/70 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="Suivant"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m9 18 6-6-6-6"></path>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  compact,
}: {
  label: string;
  value: string;
  compact?: boolean;
}) {
  return (
    <div className="bg-cream/5 border border-cream/10 rounded-xl p-4 text-center">
      <div
        className={`font-display text-gold leading-none ${
          compact ? "text-xl md:text-2xl" : "text-3xl md:text-4xl"
        }`}
      >
        {value}
      </div>
      <div
        className="text-cream/50 text-[10px] tracking-[0.25em] uppercase mt-2"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    </div>
  );
}
